/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import { QUESTION } from '@constants/terminology';
import {
  AssurancePortfolio,
  AssurancePortfolioStatus,
  AssuranceStatus,
  UniversalTrackerValueAssurance
} from '../types/assurance';
import { QuestionList, ScopeQuestion, ScopeQuestionGroup } from '../types/survey';
import { UtrvAssuranceStatus } from '../types/universalTrackerValue';

type StateAssurances = { assurances?: AssurancePortfolio[] } | undefined;


export function getQuestionAssuranceStatus(assurancePortfolio: StateAssurances, utrvId: string) {
  const firstAssurance = assurancePortfolio?.assurances?.[0];
  const utrvAssurances = firstAssurance?.universalTrackerValueAssurances;

  if (!utrvAssurances) {
    return '';
  }
  const utrvAssurance = utrvAssurances.find(u => u.utrvId === utrvId);
  if (!utrvAssurance) {
    return '';
  }

  // It's included, therefore its ether created/completed
  return AssurancePortfolioStatus.Completed === firstAssurance.status ?
    AssurancePortfolioStatus.Completed : AssurancePortfolioStatus.Created;
}

const toUtrvAssuranceStatus = (status: AssuranceStatus): UtrvAssuranceStatus | undefined => {
  switch (status) {
    case AssuranceStatus.Completed:
      return UtrvAssuranceStatus.Completed
    case AssuranceStatus.Created:
    case AssuranceStatus.Processing:
    case AssuranceStatus.Pending:
    case AssuranceStatus.PendingAfterPortfolioComplete:
      return UtrvAssuranceStatus.Created
    case AssuranceStatus.Rejected:
      return UtrvAssuranceStatus.Rejected
    default:
      return undefined;
  }
}

const assuranceQuestionReducer = (list: ScopeQuestion[], utrvMap: Map<string, UniversalTrackerValueAssurance>) => list.reduce((acc, scopeQuestion) => {
  if (scopeQuestion.utrv) {
    const assuranceUtrv = utrvMap.get(scopeQuestion.utrv._id);
    if (assuranceUtrv) {
      acc.push({
        ...scopeQuestion,
        // Override the status for utrv based on current assurance
        utrv: { ...scopeQuestion.utrv, assuranceStatus: toUtrvAssuranceStatus(assuranceUtrv.status) }
      })
    }
  }

  return acc;
}, [] as ScopeQuestion[]);

export const assuranceSurveyGroups = (universalTrackerValueAssurances: UniversalTrackerValueAssurance[], surveyGroups: ScopeQuestionGroup[]) => {
  const utrvMap = new Map(universalTrackerValueAssurances.map(v => [v.utrvId, v]))

  return surveyGroups.map(questionGroup => {
    return {
      ...questionGroup,
      list: assuranceQuestionReducer(questionGroup.list, utrvMap),
      subGroups: questionGroup.subGroups?.reduce((groupAcc, subGroup) => {
        if (subGroup.list) {
          const list = assuranceQuestionReducer(subGroup.list, utrvMap)
          if (list.length > 0) {
            groupAcc.push({ ...subGroup, list })
          }
        }
        return groupAcc;
      }, [] as QuestionList[])
    }
  });
};

export const getAssuranceLockedMessage = ({ isOrganizationManager }: { isOrganizationManager: boolean }) => {
  if (isOrganizationManager) {
    return `This ${QUESTION.SINGULAR} has been assured and approved by an external assurance partner. ` +
      `The ${QUESTION.SINGULAR} has been locked to ensure further edits aren’t made. ` +
      `If further edits need to be made to this ${QUESTION.SINGULAR}, please click 'Unlock ${QUESTION.SINGULAR}'. ` +
      `To note, this ${QUESTION.SINGULAR} will remain Assured until a new value has been submitted or verified.`
  }

  return `This ${QUESTION.SINGULAR} has been assured and approved by an external assurance partner. ` +
    `The ${QUESTION.SINGULAR} has been locked to ensure further edits aren’t made. ` +
    `If you need to make further edits to this ${QUESTION.SINGULAR}, please contact ` +
    `your organisations admin regarding unlocking this ${QUESTION.SINGULAR}.`
}
